import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/apiClient";
import { initials } from "@/lib/helpers";
import {
  LayoutDashboard, BookOpen, CalendarDays, GraduationCap, ClipboardCheck,
  ShieldCheck, Award, BarChart3, Users as UsersIcon, Bell, Sun, Moon,
  LogOut, Menu, X, GanttChartSquare, ShoppingCart, Store, Palette, Mail, Crown, Hand, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "trainer", "employee"] },
  { to: "/courses", label: "Courses", icon: BookOpen, roles: ["admin", "trainer", "employee"] },
  { to: "/sessions", label: "Sessions", icon: CalendarDays, roles: ["admin", "trainer", "employee"] },
  { to: "/my-learning", label: "My Learning", icon: GraduationCap, roles: ["employee", "admin"] },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck, roles: ["trainer", "admin"] },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, roles: ["admin", "trainer", "employee"] },
  { to: "/certificates", label: "Certificates", icon: Award, roles: ["admin", "trainer", "employee"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin"] },
  { to: "/sales", label: "Sales", icon: ShoppingCart, roles: ["admin"] },
  { to: "/orders", label: "Orders", icon: FileText, roles: ["admin"] },
  { to: "/membership", label: "Members", icon: Crown, roles: ["admin"] },
  { to: "/interests", label: "Register Interest", icon: Hand, roles: ["admin"] },
  { to: "/settings", label: "Branding", icon: Palette, roles: ["admin"] },
  { to: "/emails", label: "Emails", icon: Mail, roles: ["admin"] },
  { to: "/users", label: "People", icon: UsersIcon, roles: ["admin"] },
];

const roleLabel = { admin: "Administrator", trainer: "Trainer", employee: "Learner" };

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications/me");
      setNotifs(data);
    } catch (e) {}
  };

  useEffect(() => { loadNotifs(); }, [location.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    loadNotifs();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 dark:bg-slate-950 border-r border-slate-800 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-slate-800">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
            {branding.logo_url ? <img src={branding.logo_url} alt="logo" className="h-full w-full object-contain" /> : <GanttChartSquare className="h-5 w-5 text-white" />}
          </div>
          <div>
            <p className="font-heading font-bold text-white text-[15px] leading-none">{branding.platform_name}</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{branding.tagline}</p>
          </div>
          <button className="ml-auto lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <item.icon className="h-[18px] w-[18px]" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 border border-slate-700">
              <AvatarImage src={user.avatar} />
              <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials(user.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-slate-400 truncate">{roleLabel[user.role]}</p>
            </div>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-b border-border flex items-center px-4 sm:px-6 gap-3">
          <button className="lg:hidden text-foreground" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar-button">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />

          {user.role === "admin" && (
            <a href="/shop" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="hidden sm:flex" data-testid="view-shop-button">
                <Store className="h-4 w-4 mr-1.5" />Course Shop
              </Button>
            </a>
          )}

          {/* Notifications */}
          <DropdownMenu onOpenChange={(o) => { if (!o && unread) markAllRead(); }}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                Notifications
                {unread > 0 && <Badge variant="secondary" className="text-xs">{unread} new</Badge>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-80 overflow-y-auto">
                {notifs.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</p>}
                {notifs.map((n) => (
                  <div key={n.id} className={`px-3 py-2.5 text-sm border-b border-border last:border-0 ${!n.read ? "bg-accent/40" : ""}`}>
                    <p className="text-foreground leading-snug">{n.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 capitalize">{n.type}</p>
                  </div>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-button">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback className="bg-primary/15 text-primary text-xs">{initials(user.name)}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-medium">{user.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
